import type { ApiFailure, ApiSuccess, ImageAdjustments, MediaAsset } from "@schoolmag/contracts";
import { supabase } from "./supabase";

const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly details?: unknown) { super(message); }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  if (!supabase) throw new ApiError("Supabase is not configured.", 503);
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new ApiError("Sign in is required.", 401);
  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}`, ...init?.headers }
  });
  const payload = await response.json() as ApiSuccess<T> | ApiFailure;
  if (!response.ok || "error" in payload) {
    const failure = payload as ApiFailure;
    throw new ApiError(failure.error?.message ?? "Request failed.", response.status, failure.error?.details);
  }
  return payload.data;
}

async function authenticatedHeaders(): Promise<{ Authorization: string }> {
  if (!supabase) throw new ApiError("Supabase is not configured.", 503);
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new ApiError("Sign in is required.", 401);
  return { Authorization: `Bearer ${session.access_token}` };
}

export async function uploadMedia(path: string, file: File, width: number | null, height: number | null, adjustments?: ImageAdjustments): Promise<MediaAsset> {
  const auth = await authenticatedHeaders();
  const response = await fetch(`${apiUrl}${path}`, { method: "POST", body: file, headers: { ...auth, "Content-Type": file.type, "X-Filename": encodeURIComponent(file.name), ...(width ? { "X-Image-Width": String(width) } : {}), ...(height ? { "X-Image-Height": String(height) } : {}), ...(adjustments ? { "X-Transform-Spec": encodeURIComponent(JSON.stringify(adjustments)) } : {}) } });
  if (!response.ok) { const payload = await response.json() as ApiFailure; throw new ApiError(payload.error?.message ?? "Upload failed.", response.status); }
  const payload = await response.json() as ApiSuccess<MediaAsset>;
  return payload.data;
}

export async function mediaBlob(path: string): Promise<Blob> {
  const auth = await authenticatedHeaders();
  const response = await fetch(`${apiUrl}${path}`, { headers: auth });
  if (!response.ok) throw new ApiError("Image could not be loaded.", response.status);
  return response.blob();
}

export async function downloadFile(path: string): Promise<{ blob: Blob; filename: string }> {
  const auth = await authenticatedHeaders(); const response = await fetch(`${apiUrl}${path}`, { headers: auth });
  if (!response.ok) { const payload = await response.json() as ApiFailure; throw new ApiError(payload.error?.message ?? "Download failed.", response.status, payload.error?.details); }
  const disposition = response.headers.get("Content-Disposition") ?? ""; const filename = disposition.match(/filename="([^"]+)"/)?.[1] ?? "school-magazine.pdf";
  return { blob: await response.blob(), filename };
}
